const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Visible, focusable elements inside `root`, in document order.
 *
 * Visibility is decided from the DOM (`hidden` / `aria-hidden` on the element or
 * an ancestor) rather than from layout. Layout-based checks such as
 * `offsetParent !== null` are the usual idiom, but they are wrong for the
 * surfaces this serves: `offsetParent` is null for `position: fixed` elements —
 * which every dialog and drawer here is — and it is null for *everything* in a
 * non-layout DOM, so the trap silently degrades to "nothing is focusable" and
 * focus escapes to the page behind. The dialogs conditionally *render* controls
 * rather than hiding them with `display: none`, so an attribute check covers the
 * real cases and behaves identically in every runtime.
 */
export function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => el.closest("[hidden], [aria-hidden='true']") === null);
}

/**
 * Moves focus to the most useful element inside a newly-opened surface: the one
 * explicitly marked `data-autofocus`, else the first focusable element, else the
 * container itself (which callers make focusable with `tabIndex={-1}`).
 *
 * Focusing the container rather than nothing matters: if focus stays on the
 * trigger behind an open modal, keyboard and screen-reader users are interacting
 * with content the modal is covering.
 */
export function focusInitial(root: HTMLElement | null): void {
  if (!root) return;
  const preferred = root.querySelector<HTMLElement>("[data-autofocus]");
  const target = preferred ?? focusableWithin(root)[0] ?? root;
  target.focus();
}
